import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { getTenants } from "../api/client";
import Chart from "react-apexcharts";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { BuildingOffice2Icon, CurrencyDollarIcon, UsersIcon } from "@heroicons/react/24/outline";

ChartJS.register(ArcElement, Tooltip, Legend);

const chartPalette = ['#8A17D1', '#D326AD', '#B06BE6', '#E76BC7'];
const chartText = '#E6EEF3';
const chartGrid = 'rgba(199, 210, 254, 0.16)';

const normalizePlanName = (plan) => {
    const name = String(plan || '').trim();
    if (!name || name.toLowerCase() === 'free') return 'Basic';
    return name;
};

export default function SuperadminAnalytics() {
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        async function load() {
            try {
                const items = await getTenants();
                if (mounted) setTenants(items || []);
            } catch (e) {
                if (mounted) setError(e?.message || 'Network error');
            } finally {
                if (mounted) setLoading(false);
            }
        }
        load();
        return () => { mounted = false; };
    }, []);
    // remove reliance on global event; mobile sidebar handled locally for each page

    if (loading) return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-xl text-gray-600">Loading analytics...</div>
        </div>
    );
    if (error) return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-xl text-red-600">{error}</div>
        </div>
    );

    // compute summary metrics
    const totalTenants = tenants.length;
    const totalUsers = tenants.reduce((s, t) => s + (t.userCount || t.UserCount || 0), 0);
    const totalRevenue = tenants.reduce((s, t) => s + (t.salesTotal || t.SalesTotal || 0), 0);

    // plan distribution
    const planMap = {};
    tenants.forEach(t => {
        const plan = normalizePlanName(t.subscriptionPlan || t.SubscriptionPlan);
        planMap[plan] = (planMap[plan] || 0) + 1;
    });
    const planLabels = Object.keys(planMap);
    const planValues = Object.values(planMap);

    const doughnutData = {
        labels: planLabels,
        datasets: [{
            data: planValues,
            backgroundColor: chartPalette,
            borderColor: '#0B1220',
            borderWidth: 3,
        }]
    };

    const apexOptions = {
        chart: { id: 'tenants-by-plan', toolbar: { show: false }, foreColor: chartText },
        colors: ['#8A17D1'],
        plotOptions: { bar: { borderRadius: 5, columnWidth: '55%' } },
        dataLabels: { enabled: false },
        grid: { borderColor: chartGrid },
        xaxis: {
            categories: planLabels,
            labels: { style: { colors: chartText } },
            axisBorder: { color: chartGrid },
            axisTicks: { color: chartGrid },
        },
        yaxis: { labels: { style: { colors: chartText } } },
        tooltip: { theme: 'dark' },
    };
    const apexSeries = [{ name: 'Tenants', data: planValues }];
    const doughnutOptions = {
        plugins: {
            legend: { labels: { color: chartText, padding: 16 } },
            tooltip: { backgroundColor: '#071026', titleColor: '#FFFFFF', bodyColor: chartText },
        },
    };

    return (
        <>
        <Header />
        <div className="min-h-screen bg-gray-50">
            <div className="grid grid-cols-12">
                <aside className="col-span-2 hidden lg:flex">
                    <Sidebar />
                </aside>


                <main className="col-span-12 lg:col-span-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex items-start justify-between mb-8">
                        <div className="flex items-center gap-3">
                            <div>
                                <h1 className="text-3xl font-extrabold text-gray-900">Analytics</h1>
                                <p className="mt-2 text-sm text-gray-500">Key metrics and trends across tenants and usage.</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button className="inline-flex items-center px-4 py-2 bg-white border rounded-md text-sm font-medium shadow-sm hover:shadow focus:outline-none">
                                Export
                            </button>
                            <select className="px-3 py-2 rounded-md border bg-white text-sm">
                                <option>Last 7 days</option>
                                <option>Last 30 days</option>
                                <option>Last 90 days</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-8">
                        <MetricCard title="Active Users" value={totalUsers.toLocaleString()} variant="purple" icon={<UsersIcon className="h-6 w-6" />} />
                        <MetricCard title="Monthly Revenue" value={`₦${(Number(totalRevenue)||0).toLocaleString()}`} variant="magenta" icon={<CurrencyDollarIcon className="h-6 w-6" />} />
                        <MetricCard title="Total Tenants" value={totalTenants} variant="lavender" icon={<BuildingOffice2Icon className="h-6 w-6" />} />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white p-6 rounded-lg shadow">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-gray-900">Tenants by Subscription Plan</h3>
                                <span className="text-sm text-gray-500">Summary</span>
                            </div>
                            <div className="h-64">
                                <Chart options={apexOptions} series={apexSeries} type="bar" height={250} />
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-gray-900">Subscription Distribution</h3>
                                <span className="text-sm text-gray-500">Distribution</span>
                            </div>
                            <div className="h-64 flex items-center justify-center">
                                <Doughnut data={doughnutData} options={doughnutOptions} />
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
        </>
    );
}

function MetricCard({ title, value, variant, icon }) {
    const variants = {
        purple: 'bg-[#8A17D1]/15 text-[#d9a7ff] ring-[#8A17D1]/30',
        magenta: 'bg-[#D326AD]/15 text-[#f3a1dc] ring-[#D326AD]/30',
        lavender: 'bg-[#B06BE6]/15 text-[#d9a7ff] ring-[#B06BE6]/30',
    };

    return (
        <article className="rounded-xl border border-[#C7D2FE]/15 bg-[#111B31] p-5 shadow-lg shadow-[#020617]/30">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <div className="text-sm font-medium text-[#C7D2FE]">{title}</div>
                    <div className="mt-2 text-2xl font-bold text-[#E6EEF3]">{value}</div>
                </div>
                <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-1 ${variants[variant] || variants.purple}`}>
                    {icon}
                </div>
            </div>
        </article>
    );
}
